import React from 'react';
import { TutorialModal, TutorialStep, isTutorialCompleted } from './TutorialModal';

const HIDER_TUTORIAL_KEY = 'hider';

const hiderSteps: TutorialStep[] = [
  {
    title: 'Welcome, Hider!',
    bullets: [
      "You'll create a scavenger hunt for hunters to explore",
      'Place up to 10 hiding locations called "Pins"',
      'Each Pin can hold up to 10 hidden items',
      'The final Pin should contain the grand treasure',
    ],
    image: '/tutorial/hider-1.png',
  },
  {
    title: 'Prepare Before You Go',
    bullets: [
      'Collect all items you plan to hide',
      "Choose unique items that won't be mistaken for trash",
      'Plan any optional challenges in advance',
      'Know your hiding area before arriving',
    ],
    image: '/tutorial/hider-2.png',
  },
  {
    title: 'Place Your Pins',
    bullets: [
      'Tap the map to drop a Pin at your current location',
      'Hide items within 20 meters of the Pin center',
      "Don't place items in plain sight",
      'Avoid hazardous or restricted areas',
    ],
    image: '/tutorial/hider-3.png',
  },
  {
    title: 'Verify Your Location',
    bullets: [
      'Ensure GPS accuracy is strong (green indicator)',
      'Confirm you have cellphone signal',
      'Verify the area is accessible to hunters',
      'Make sure the location is safe',
    ],
    image: '/tutorial/hider-4.png',
  },
  {
    title: 'Take a Hint Photo',
    bullets: [
      'Take a photo showing the Pin area',
      'This helps hunters identify the general location',
      "Don't reveal exact hiding spots",
      'Make it recognizable but not obvious',
    ],
    image: '/tutorial/hider-5.png',
  },
  {
    title: 'Add Challenges (Optional)',
    bullets: [
      'Riddle: A one-word answer based on context',
      'Hex Code: A physical code placed at the location',
      'Numerical: A countable answer unlikely to change',
      'Challenges are revealed after hunters attempt completion',
    ],
    image: '/tutorial/hider-6.png',
  },
  {
    title: 'Challenge: Riddle',
    bullets: [
      'Write a riddle with a one-word answer',
      'The answer should relate to the location',
      'Example: "I give shade but no roof" → TREE',
      'Hunters type their answer to proceed',
    ],
    image: '/tutorial/hider-6a.png',
  },
  {
    title: 'Challenge: Hex Code',
    bullets: [
      'Place a physical sign with a 4-character code',
      'You can write it in the dirt, on a piece of paper, or use chalk... How creative you get is up to you!',
      'Use letters A-F and numbers 0-9 (e.g., A3F9)',
      'Hide the code near the Pin location',
      'Hunters must find and enter the code',
    ],
    image: '/tutorial/hider-6b.png',
  },
  {
    title: 'Challenge: Numerical',
    bullets: [
      'Ask a question with a number answer',
      'Base it on something countable at the location',
      'Example: "How many benches are nearby?" → 4',
      'Choose things unlikely to change',
    ],
    image: '/tutorial/hider-6c.png',
  },
  {
    title: 'Mark Yourself Ready',
    bullets: [
      'Press "I\'m Ready" after placing your pins',
      'Wait for all other hiders to confirm',
      'Once all hiders are ready, hunters will confirm',
      'The hunt begins when everyone is ready',
    ],
    image: '/tutorial/hider-7.png',
  },
  {
    title: 'Referee Mode',
    bullets: [
      'Watch hunters search for items on your screen',
      'Validate items when hunters mark them found',
      'Reset invalid finds if needed',
      'Provide hints if hunters struggle',
    ],
    image: '/tutorial/hider-8.png',
  },
  {
    title: "You're Ready!",
    bullets: [
      'Prepare all items and challenges beforehand',
      'Verify GPS and signal at each Pin',
      'Hide items within 20 meters, not in plain sight',
      'Enjoy watching hunters discover your creations!',
    ],
    image: '/tutorial/hider-9.png',
  },
];

interface HiderTutorialProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

/**
 * Hider onboarding tutorial
 */
export const HiderTutorial: React.FC<HiderTutorialProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  return (
    <TutorialModal
      isOpen={isOpen}
      onClose={onClose}
      onComplete={onComplete}
      steps={hiderSteps}
      tutorialKey={HIDER_TUTORIAL_KEY}
    />
  );
};

/**
 * Check if the hider tutorial has been completed
 */
export function isHiderTutorialCompleted(): boolean {
  return isTutorialCompleted(HIDER_TUTORIAL_KEY);
}

export default HiderTutorial;
